import { Injectable } from '@angular/core';
import { formatDate } from '@angular/common';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { UnifiedUnpaidPayment } from './unpaid-payments-adapter.service';

dayjs.extend(utc);
dayjs.extend(timezone);

const LA_TIMEZONE = 'America/Los_Angeles';

export interface UnpaidListDateRange {
  startDate: string;
  endDate: string;
}

export interface UnpaidListDateRangeBounds {
  min?: string;
  max?: string;
}

export interface UnpaidPayAllLabelParams {
  fmFilter: number;
  fmStatusFilter?: string;
  fmPartnerStatus?: string;
}

export interface UnpaidPaymentNoteEntry {
  userName: string;
  timestamp: string;
  action: string;
  note: string;
  sortKey: string;
}

@Injectable({
  providedIn: 'root',
})
export class UnpaidPaymentsCalculationService {
  /** Today (YYYY-MM-DD) in LA — matches `app-selection` date-picker bounds. */
  unpaidPaymentsTodayYmd(): string {
    return dayjs().tz(LA_TIMEZONE).format('YYYY-MM-DD');
  }

  clampYmdToRange(value: string | undefined | null, min: string, max: string): string {
    const v = (value ?? '').trim();
    if (!v) return min;
    if (v < min) return min;
    if (v > max) return max;
    return v;
  }

  /** Clamp list filter dates so neither exceeds `max` (defaults to today) and start <= end. */
  clampUnpaidListDateRange(
    start: string,
    end: string,
    options?: UnpaidListDateRangeBounds
  ): UnpaidListDateRange {
    const min = options?.min ?? '2017-01-01';
    const max = options?.max ?? this.unpaidPaymentsTodayYmd();
    let endDate = this.clampYmdToRange(end, min, max);
    let startDate = this.clampYmdToRange(start, min, max);
    if (startDate > endDate) {
      startDate = endDate;
    }
    return { startDate, endDate };
  }

  /** Upper bound for the start-date picker: min(selected end, today). */
  resolveListStartDateMax(endDate: string, maxDate?: string): string {
    const today = maxDate ?? this.unpaidPaymentsTodayYmd();
    return endDate && endDate < today ? endDate : today;
  }

  /** Total record count from unpaid list APIs (`count` is the source of truth for paging). */
  resolveTotalRecords(response: unknown): number {
    const res = response as Record<string, unknown> | null | undefined;
    const totalRecords = Number(
      res?.['count'] ?? res?.['number_of_records'] ?? res?.['total_records'] ?? 0
    );
    return Number.isFinite(totalRecords) && totalRecords > 0 ? totalRecords : 0;
  }

  /** Total pages derived from API `count` and page size. */
  resolveApiPageCount(response: unknown, pageSize: number): number {
    const totalRecords = this.resolveTotalRecords(response);
    if (totalRecords > 0 && pageSize > 0) {
      return Math.ceil(totalRecords / pageSize);
    }

    const res = response as Record<string, unknown> | null | undefined;
    const apiPages = Number(res?.['number_of_pages'] ?? res?.['total_pages']);
    if (Number.isFinite(apiPages) && apiPages >= 0) {
      return apiPages;
    }

    return 0;
  }

  /** When the current page is past the API page count, return a safe page to load. */
  clampPageToApiTotal(currentPage: number, apiPageCount: number): number | null {
    if (apiPageCount <= 0) {
      return currentPage > 1 ? 1 : null;
    }
    if (currentPage > apiPageCount) {
      return apiPageCount;
    }
    if (currentPage < 1) {
      return 1;
    }
    return null;
  }

  /**
   * Title-case label for the overview “Pay all” action, aligned with legacy unpaid-payments UI.
   */
  resolveUnpaidPayAllLabel({
    fmFilter: filter,
    fmStatusFilter = '0',
    fmPartnerStatus = '0',
  }: UnpaidPayAllLabelParams): string {
    const activeStatus = fmStatusFilter;
    const partnerStatus = fmPartnerStatus;

    if (filter === 10) {
      return 'Pay All to Businesses';
    }

    if (filter === 1 || filter === 6) {
      if (partnerStatus === '1') return 'Pay All to Active Partners';
      if (partnerStatus === '2') return 'Pay All to Inactive Partners';
      return 'Pay All to Partners';
    }

    if (filter === 2) {
      return 'Pay All to Paul';
    }

    if (filter === 3) {
      if (activeStatus === '1') return 'Pay All to Active Protected';
      if (activeStatus === '2') return 'Pay All to Inactive Protected';
      return 'Pay All to Protected';
    }

    if ((filter === 0 || filter === 4) && activeStatus === '3') {
      return 'Pay All to Partners';
    }

    if (filter === 9 && activeStatus === '1') {
      return 'Pay All to Partners';
    }

    const businessFiltersStatus0 = [0, 4, 5, 8, 9, 11, 12, 13, 14, 15];
    const businessFiltersStatus12 = [0, 4, 5, 8, 11, 12, 15];

    if (businessFiltersStatus0.includes(filter) && activeStatus === '0') {
      return 'Pay All to Businesses';
    }
    if (businessFiltersStatus12.includes(filter) && activeStatus === '1') {
      return 'Pay All to Active Businesses';
    }
    if (businessFiltersStatus12.includes(filter) && activeStatus === '2') {
      return 'Pay All to Inactive Businesses';
    }

    return 'Pay All to Businesses';
  }

  /** Display calendar dates in unpaid-payments tables as `MM/DD/YYYY` in LA timezone. */
  formatUnpaidTableDate(value: unknown): string {
    if (value == null || value === '' || value === '-') {
      return '-';
    }

    const raw = String(value).trim();
    
    // If it's strictly just a date (YYYY-MM-DD) with no time component, format it directly
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const parts = raw.split('-');
      return `${parts[1]}/${parts[2]}/${parts[0]}`;
    }

    // If it's already in MM/DD/YYYY format, return it directly
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
      return raw;
    }

    const d = dayjs(raw);
    if (d.isValid()) {
      // If the string contains a 'Z' or '+00:00', dayjs parses it as UTC automatically.
      // If it doesn't have a timezone, we assume it's UTC from the server.
      const isUtcFormat = raw.toUpperCase().includes('Z') || raw.includes('+00');
      const dateObj = isUtcFormat ? d : dayjs.utc(raw);
      return dateObj.tz(LA_TIMEZONE).format('MM/DD/YYYY');
    }

    return raw;
  }

  /** True when the unpaid list API marks a row as transfer-disabled (legacy: `transfers_disabled != 1`). */
  isRowTransferDisabled(payment: UnifiedUnpaidPayment): boolean {
    const raw = payment['transfers_disabled'];
    if (raw === undefined || raw === null || raw === '') {
      return false;
    }
    if (raw === 1 || raw === '1') {
      return false;
    }
    return true;
  }

  /** Copy for the Upcoming Amount overview card info tooltip. */
  formatUpcomingAmountTooltip(amount: number): string {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(amount) ? amount : 0);
    return `Your upcoming payment amount for the next 2 days is ${formatted}.`;
  }

  countPayableUnpaidRows(
    rows: UnifiedUnpaidPayment[],
    isRowPayable: (payment: UnifiedUnpaidPayment) => boolean
  ): number {
    let count = 0;
    for (const row of rows) {
      if (isRowPayable(row)) {
        count++;
      }
    }
    return count;
  }

  listingHasAllRows(pageRows: unknown[], totalCount: number): boolean {
    return totalCount > 0 && pageRows.length >= totalCount;
  }

  resolvePayableCountFromListing(
    listPayments: (limit: number, offset: number) => Observable<any>,
    totalCount: number,
    pageRows: UnifiedUnpaidPayment[],
    isRowPayable: (payment: UnifiedUnpaidPayment) => boolean
  ): Observable<number> {
    if (!totalCount) {
      return of(0);
    }

    if (this.listingHasAllRows(pageRows, totalCount)) {
      return of(this.countPayableUnpaidRows(pageRows, isRowPayable));
    }

    return listPayments(totalCount, 0).pipe(
      map((res) =>
        this.countPayableUnpaidRows(Array.isArray(res?.payments) ? res.payments : [], isRowPayable)
      )
    );
  }

  /** Normalizes urgent-note API rows for the shared unpaid notes modal. */
  mapUnpaidUrgentNotesToEntries(notes: unknown[]): UnpaidPaymentNoteEntry[] {
    if (!Array.isArray(notes)) return [];

    return notes
      .map((item) => {
        const note = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
        const sortSource = note['date_added'] ?? note['created_at'] ?? '';
        return {
          userName: this.readUserName(note),
          timestamp: this.formatNoteTimestamp(sortSource),
          action: this.readNoteAction(note),
          note: this.readNoteBody(note),
          sortKey: String(sortSource || ''),
        };
      })
      .sort((a, b) => b.sortKey.localeCompare(a.sortKey));
  }

  private resolveNoteTimezone(): string | undefined {
    return (
      localStorage.getItem('timezoneName') ||
      localStorage.getItem('timezone') ||
      sessionStorage.getItem('timezone') ||
      undefined
    );
  }

  private formatNoteTimestamp(value: unknown): string {
    if (value == null || value === '') return '';

    const timezone = this.resolveNoteTimezone();
    const parsed = new Date(String(value));
    if (Number.isNaN(parsed.getTime())) {
      return String(value);
    }

    try {
      const formatted = formatDate(parsed, 'MMM d, yyyy, h:mm:ss a', 'en-US', timezone);
      const timezoneLabel =
        localStorage.getItem('timezoneName') ||
        (timezone && !/^-?\d+$/.test(String(timezone)) ? String(timezone) : '');
      return timezoneLabel ? `${formatted} (${timezoneLabel})` : formatted;
    } catch {
      return String(value);
    }
  }

  private readNoteAction(note: Record<string, unknown>): string {
    const raw = note['action'] ?? note['action_type'] ?? note['action_label'];
    if (raw == null || raw === '') return '-';
    return String(raw);
  }

  private readNoteBody(note: Record<string, unknown>): string {
    const raw = note['admin_notes'] ?? note['notes'] ?? note['note'];
    if (raw == null || raw === '') return '-';
    return String(raw);
  }

  private readUserName(note: Record<string, unknown>): string {
    const raw = note['admin_name'] ?? note['added_by'] ?? note['admin'] ?? note['created_by'];
    if (raw == null || raw === '') return 'Unknown';
    return String(raw);
  }
}
